import type {
    AnalyticsRangeKey,
    BillingCycle,
    CustomerStatus,
    InvoiceStatus,
    PaymentMethod,
    RangeKey,
    TeamRole
} from '@/types'

export const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
    active: 'Aktif',
    trial: 'Trial',
    'at-risk': 'Berisiko',
    canceled: 'Dibatalkan'
}

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
    paid: 'Lunas',
    pending: 'Menunggu',
    failed: 'Gagal',
    refunded: 'Dikembalikan'
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    va: 'Virtual account',
    qris: 'QRIS',
    card: 'Kartu kredit',
    transfer: 'Transfer bank',
    ewallet: 'Dompet digital'
}

export const CYCLE_LABELS: Record<BillingCycle, string> = {
    monthly: 'Bulanan',
    annual: 'Tahunan'
}

export const ROLE_LABELS: Record<TeamRole, string> = {
    owner: 'Pemilik',
    admin: 'Admin',
    finance: 'Keuangan',
    support: 'Dukungan',
    viewer: 'Pengamat'
}

export const ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
    owner: 'Akses penuh termasuk penagihan dan penghapusan workspace.',
    admin: 'Kelola pelanggan, tagihan, tim, dan pengaturan.',
    finance: 'Kelola tagihan dan ekspor laporan keuangan.',
    support: 'Lihat dan perbarui data pelanggan.',
    viewer: 'Hanya bisa melihat dashboard dan laporan.'
}

export const RANGE_OPTIONS: { value: RangeKey, label: string, days: number, comparison: string }[] = [
    { value: '7d', label: '7 hari', days: 7, comparison: 'vs 7 hari sebelumnya' },
    { value: '30d', label: '30 hari', days: 30, comparison: 'vs 30 hari sebelumnya' },
    { value: '90d', label: '90 hari', days: 90, comparison: 'vs 90 hari sebelumnya' },
    { value: '12m', label: '12 bulan', days: 365, comparison: 'vs 12 bulan sebelumnya' }
]

export const ANALYTICS_RANGE_OPTIONS: { value: AnalyticsRangeKey, label: string, months: number }[] = [
    { value: '3m', label: '3 bulan', months: 3 },
    { value: '6m', label: '6 bulan', months: 6 },
    { value: '12m', label: '12 bulan', months: 12 }
]

export const CITIES: { city: string, province: string, weight: number }[] = [
    { city: 'Jakarta Selatan', province: 'DKI Jakarta', weight: 9 },
    { city: 'Jakarta Pusat', province: 'DKI Jakarta', weight: 5 },
    { city: 'Jakarta Barat', province: 'DKI Jakarta', weight: 5 },
    { city: 'Jakarta Utara', province: 'DKI Jakarta', weight: 3 },
    { city: 'Jakarta Timur', province: 'DKI Jakarta', weight: 4 },
    { city: 'Bandung', province: 'Jawa Barat', weight: 7 },
    { city: 'Bekasi', province: 'Jawa Barat', weight: 4 },
    { city: 'Depok', province: 'Jawa Barat', weight: 3 },
    { city: 'Bogor', province: 'Jawa Barat', weight: 3 },
    { city: 'Surabaya', province: 'Jawa Timur', weight: 7 },
    { city: 'Malang', province: 'Jawa Timur', weight: 3 },
    { city: 'Semarang', province: 'Jawa Tengah', weight: 4 },
    { city: 'Surakarta', province: 'Jawa Tengah', weight: 2 },
    { city: 'Yogyakarta', province: 'DI Yogyakarta', weight: 4 },
    { city: 'Tangerang', province: 'Banten', weight: 5 },
    { city: 'Denpasar', province: 'Bali', weight: 4 },
    { city: 'Medan', province: 'Sumatera Utara', weight: 5 },
    { city: 'Palembang', province: 'Sumatera Selatan', weight: 2 },
    { city: 'Pekanbaru', province: 'Riau', weight: 2 },
    { city: 'Padang', province: 'Sumatera Barat', weight: 2 },
    { city: 'Batam', province: 'Kepulauan Riau', weight: 2 },
    { city: 'Makassar', province: 'Sulawesi Selatan', weight: 3 },
    { city: 'Manado', province: 'Sulawesi Utara', weight: 1 },
    { city: 'Balikpapan', province: 'Kalimantan Timur', weight: 2 },
    { city: 'Pontianak', province: 'Kalimantan Barat', weight: 1 }
]

export const PROVINCES: string[] = Array.from(new Set(CITIES.map((item) => item.province))).sort()

export const getProvince = (city: string): string => CITIES.find((item) => item.city === city)?.province ?? 'Lainnya'
